/**
 * Nome do arquivo a partir de um header Content-Disposition (RFC 6266).
 * Tenta a forma estendida `filename*=UTF-8''...` primeiro — é a que o
 * ASP.NET Core prioriza e a única com suporte a acentos — e cai para
 * `filename="..."` ou `filename=...` sem aspas.
 */
export const resolveContentDispositionFileName = (
    disposition: string | undefined
): string | null => {
    if (!disposition) return null;

    const extended = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (extended?.[1]) {
        try {
            return decodeURIComponent(extended[1].trim());
        } catch {
            // cai pro fallback abaixo se vier mal-formado
        }
    }

    const plain = disposition.match(/filename="([^"]+)"|filename=([^;]+)/i);
    return (plain?.[1] ?? plain?.[2])?.trim() ?? null;
};

/** Salva um Blob já baixado como arquivo na máquina do usuário. */
export const saveBlobAsFile = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();

    // Revoga depois de dar tempo do navegador iniciar a leitura do blob —
    // revogar no mesmo tick pode abortar o download em alguns browsers.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

/**
 * Mensagem de erro de uma request feita com `responseType: "blob"`, onde o
 * corpo do erro também chega como Blob e não dá pra ler direto pelo
 * `errorUtil`. Devolve null quando não há texto do backend (erro de rede,
 * 500 sem corpo).
 */
export const readBlobErrorMessage = async (
    error: unknown
): Promise<string | null> => {
    const data = (error as { response?: { data?: unknown } })?.response?.data;

    if (!(data instanceof Blob)) return null;

    try {
        const raw = (JSON.parse(await data.text()) as { message?: string[] | string })
            ?.message;
        const message = Array.isArray(raw) ? raw[0] : raw;

        return message && message.trim() !== "" ? message : null;
    } catch {
        return null;
    }
};
